import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Truck, Package, MapPin, DollarSign, Clock, Power, 
  Navigation, Bell, ChevronRight, Star, TrendingUp,
  Wallet, CheckCircle, AlertCircle
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const OperatorDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [operator, setOperator] = useState(null);
  const [availableJobs, setAvailableJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get operator profile
      const profileRes = await api.get('/operators/me');
      setOperator(profileRes.data.operator);
      setIsOnline(profileRes.data.operator?.isOnline || false);

      // Get active job if any
      const activeRes = await api.get('/operators/active-job');
      setActiveJob(activeRes.data.activeJob);

      // Get available jobs
      if (profileRes.data.operator?.isOnline) {
        const jobsRes = await api.get('/operators/available-jobs');
        setAvailableJobs(jobsRes.data.jobs || []);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        navigate('/operator/apply');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load dashboard',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleOnlineStatus = async () => {
    setTogglingStatus(true);
    try {
      const newStatus = !isOnline;
      await api.put('/operators/online-status', null, {
        params: { is_online: newStatus }
      });
      
      setIsOnline(newStatus);
      toast({
        title: newStatus ? 'You are now Online' : 'You are now Offline',
        description: newStatus ? 'You can now receive job requests' : 'You will not receive new jobs'
      });

      // Refresh jobs if going online
      if (newStatus) {
        const jobsRes = await api.get('/operators/available-jobs');
        setAvailableJobs(jobsRes.data.jobs || []);
      } else {
        setAvailableJobs([]);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    } finally {
      setTogglingStatus(false);
    }
  };

  const acceptJob = async (jobId) => {
    try {
      await api.post(`/operators/jobs/${jobId}/accept`);
      toast({
        title: 'Job Accepted!',
        description: 'Navigate to pickup location'
      });
      navigate('/operator/active-job');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to accept job',
        variant: 'destructive'
      });
    }
  };

  const formatPrice = (price) => `K${price?.toFixed(2) || '0.00'}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className={`p-6 ${isOnline ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-gray-600 to-gray-700'} text-white`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Truck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">{operator?.name || 'Operator'}</h1>
              <p className="text-sm opacity-80">
                {operator?.vehicleMake} {operator?.vehicleModel}
              </p>
            </div>
          </div>
          <Button
            onClick={toggleOnlineStatus}
            disabled={togglingStatus}
            variant={isOnline ? 'secondary' : 'default'}
            className={isOnline ? 'bg-white text-green-600' : 'bg-orange-500'}
            data-testid="toggle-online-btn"
          >
            <Power className="h-4 w-4 mr-2" />
            {togglingStatus ? 'Updating...' : isOnline ? 'Go Offline' : 'Go Online'}
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <Wallet className="h-5 w-5 mx-auto mb-1" />
            <p className="text-lg font-bold">{formatPrice(operator?.walletBalance)}</p>
            <p className="text-xs opacity-80">Balance</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <Package className="h-5 w-5 mx-auto mb-1" />
            <p className="text-lg font-bold">{operator?.totalDeliveries || 0}</p>
            <p className="text-xs opacity-80">Deliveries</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3 text-center">
            <Star className="h-5 w-5 mx-auto mb-1" />
            <p className="text-lg font-bold">{operator?.rating?.toFixed(1) || '5.0'}</p>
            <p className="text-xs opacity-80">Rating</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Active Job Alert */}
        {activeJob && (
          <Card 
            className="p-4 bg-orange-50 border-orange-300 cursor-pointer"
            onClick={() => navigate('/operator/active-job')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                  <Navigation className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-orange-700">Active Delivery</p>
                  <p className="text-sm text-orange-600">{activeJob.status?.replace('_', ' ')}</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-orange-500" />
            </div>
          </Card>
        )}

        {/* Offline Notice */}
        {!isOnline && !activeJob && (
          <Card className="p-6 text-center bg-gray-50">
            <Power className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-700 mb-1">You're Offline</h3>
            <p className="text-sm text-gray-500 mb-4">Go online to start receiving job requests</p>
            <Button 
              onClick={toggleOnlineStatus}
              disabled={togglingStatus}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Power className="h-4 w-4 mr-2" />
              Go Online
            </Button>
          </Card>
        )}

        {/* Available Jobs */}
        {isOnline && !activeJob && (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Bell className="h-5 w-5 text-orange-500" />
                Available Jobs
              </h2>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={fetchData}
              >
                Refresh
              </Button>
            </div>

            {availableJobs.length === 0 ? (
              <Card className="p-8 text-center">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <h3 className="font-medium text-gray-700 mb-1">No Jobs Available</h3>
                <p className="text-sm text-gray-500">New job requests will appear here</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {availableJobs.map((job) => (
                  <Card key={job.id} className="p-4" data-testid={`job-${job.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-mono text-sm text-orange-600">{job.trackingCode}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(job.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <p className="text-xl font-bold text-green-600">{formatPrice(job.price)}</p>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5" />
                        <p className="text-sm text-gray-600 flex-1">{job.pickupAddress}</p>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-red-500 mt-1.5" />
                        <p className="text-sm text-gray-600 flex-1">{job.dropoffAddress}</p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span className="flex items-center gap-1">
                        <Package className="h-4 w-4" />
                        {job.cargo?.weightKg} kg
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.distanceKm?.toFixed(1)} km
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        job.cargo?.category === 'fragile' ? 'bg-yellow-100 text-yellow-700' :
                        job.cargo?.category === 'refrigerated' ? 'bg-blue-100 text-blue-700' :
                        job.cargo?.category === 'hazardous' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {job.cargo?.category}
                      </span>
                    </div>

                    <Button 
                      onClick={() => acceptJob(job.id)}
                      className="w-full bg-green-500 hover:bg-green-600"
                      data-testid={`accept-job-${job.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accept Job
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <Card 
            className="p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => navigate('/operator/earnings')}
          >
            <TrendingUp className="h-6 w-6 text-green-500 mb-2" />
            <p className="font-medium">Earnings</p>
            <p className="text-sm text-gray-500">View your earnings</p>
          </Card>
          <Card 
            className="p-4 cursor-pointer hover:bg-gray-50"
            onClick={() => navigate('/operator/history')}
          >
            <Clock className="h-6 w-6 text-blue-500 mb-2" />
            <p className="font-medium">History</p>
            <p className="text-sm text-gray-500">Past deliveries</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OperatorDashboard;
